import { useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../../lib/api";
import { dgcaMaxRefund, isDgcaEligibleTicket } from "./CancellationPanel";

// Whole Trip shown as one ticket: Step 3, sending the refund to the customer,
// is done once for both flights. The server sends ONE message (chat card +
// push) as soon as both flights carry charges, so this card just saves the
// missing ones: a free (DGCA) flight is sent as ₹0 charge once TBO confirmed;
// a normal flight's charge is saved in its own Step 1.

const AFTER_SEND = ["customer_accepted", "cancellation_processed", "refund_processed", "cancelled_at_airline"];
const CLOSED = ["abandoned", "expired", "customer_declined", "blocked"];

export function flightSendState(ticket, booking, formatINR) {
  const s = ticket?.cancellation_status || "";
  if (s === "charges_notified") {
    return { key: "sent", text: `Sent: refund ${formatINR(Number(ticket.refund_amount_calculated) || 0)}` };
  }
  if (AFTER_SEND.includes(s)) return { key: "past", text: s === "customer_accepted" ? "Customer accepted" : "Cancelled" };
  if (CLOSED.includes(s)) return { key: "closed", text: "Customer did not go ahead" };
  if (isDgcaEligibleTicket(ticket)) {
    return Number(ticket.tbo_dgca_confirmed) === 1
      ? { key: "ready", dgca: true, text: `Ready: refund ${formatINR(dgcaMaxRefund(booking))}, no airline fee` }
      : { key: "blocked", text: "Check with TBO first (Step 1 of this flight)" };
  }
  return { key: "blocked", text: "Save the airline fee first (Step 1 of this flight)" };
}

const TONE = {
  sent: "text-green-700",
  past: "text-green-700",
  ready: "text-blue-700",
  blocked: "text-amber-700",
  closed: "text-gray-500",
};

export default function WholeTripSendCard({ flights, primaryTicketId, formatINR, onSent }) {
  const [busy, setBusy] = useState(false);
  const rows = flights.map((f) => ({ ...f, st: flightSendState(f.ticket, f.booking, formatINR) }));
  if (rows.some((r) => !r.ticket)) return null;
  if (rows.every((r) => r.st.key === "past" || r.st.key === "closed")) return null;

  const canSend = rows.every((r) => r.st.key === "ready" || r.st.key === "sent") && rows.some((r) => r.st.key === "ready");
  const allSent = rows.every((r) => r.st.key === "sent");
  const blocked = rows.filter((r) => r.st.key === "blocked");

  const send = async () => {
    setBusy(true);
    try {
      for (const r of rows) {
        if (r.st.key === "ready" && r.st.dgca) {
          await api.post("/admin/tickets/update-cancellation-charge", { ticket_id: Number(r.ticket.ticket_id), cancellation_charge: 0 });
        }
      }
      toast.success("Sent to the customer as one message for both flights.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not send to the customer.");
    } finally {
      setBusy(false);
      await onSent?.();
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/admin/tickets/notify-customer", {
        ticket_id: Number(primaryTicketId),
        notification_type: "CANCELLATION_FEE_QUOTED",
      });
      toast.success("Sent again to the customer (both flights).");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not send again.");
    } finally {
      setBusy(false);
      await onSent?.();
    }
  };

  return (
    <div className="border-2 border-blue-200 rounded-2xl overflow-hidden bg-white" data-testid="whole-trip-send">
      <div className="bg-blue-50 px-4 py-3 flex items-center gap-2 border-b border-blue-100">
        <span className="shrink-0 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">Step 3</span>
        <span className="text-sm font-semibold text-gray-900">Send to customer (both flights)</span>
      </div>
      <div className="p-4 space-y-3 text-sm">
        <p className="text-gray-600">
          The customer gets one message with both flights and the total, and confirms once. Their Accept
          cancels both flights and refunds them automatically.
        </p>
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.label} className="flex justify-between gap-3">
              <span className="font-medium text-gray-800">{r.label}{r.route ? ` · ${r.route}` : ""}</span>
              <span className={`font-semibold ${TONE[r.st.key]}`}>{r.st.text}</span>
            </li>
          ))}
        </ul>
        {allSent ? (
          <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-green-800 flex items-center justify-between gap-3">
            <span>Sent as one message. Waiting for the customer to accept.</span>
            <button
              type="button"
              onClick={resend}
              disabled={busy}
              className="shrink-0 rounded-lg border border-green-300 bg-white px-3 py-1 text-xs font-semibold hover:bg-green-100 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send again"}
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={send}
              disabled={!canSend || busy}
              className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-white font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send to customer (both flights)"}
            </button>
            {blocked.length > 0 && (
              <p className="text-xs text-amber-700">Finish Step 1 for {blocked.map((r) => r.label).join(" and ")} first.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
